import { registerLocaleData } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from 'src/utils/app.constants';
import { LocalStorage } from 'src/utils/local-storage';
import { AuthService } from 'src/services/auth-services/auth.service';
import localeEn from '@angular/common/locales/en-CA';
import localeFr from '@angular/common/locales/fr-CA';
import { environment } from 'src/environments/environment';
import { userProfile } from './utility/Objects/userProfile';
import { Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy {
  public isUserAuthenticated: boolean;

  private inactivityTimeout: any;
  private checkInactivityAfterUptimeTimer: any;

  private readonly INACTIVITY_LIMIT = 5 * 60 * 1000; // 5 minutes inactive limit
  private readonly UPTIME_LIMIT = 3 * 60 * 60 * 1000; // 3 hours time limit
  private isEligibleForReload = false;
  private readonly LAST_RELOAD_KEY = 'lastReloadTimestamp';

  constructor(
    private translateService: TranslateService,
    private localStorage: LocalStorage,
    private authService: AuthService,
    private router: Router
  ) {

    if (!this.authService.isAuthenticated()) {
      // check if there is an userProfileKey item already set in localstorage
      // if so, use that to authenticate the user first
      const userProfileItem = JSON.parse(LocalStorage.getUserProfileItem());
      if (!!userProfileItem) {
        let importedUserProfile = new userProfile();
        for (let key in userProfileItem) {
          importedUserProfile[key] = userProfileItem[key];
        }
        this.authService.importUserProfileToStore(importedUserProfile);
        this.localStorage.removeImportedUserProfile();

        // If we're in this block it means this is a new login from the hub
        // Check if the user tried to go to a specific page and redirect them if so
        const localStorageReturnPath = this.localStorage.getReturnPath();
        if (!!localStorageReturnPath && localStorageReturnPath !== '') {
          this.localStorage.clearReturnPath();
          this.router.navigate([localStorageReturnPath]);
        }
      }
    }

    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeFr, 'fr');

    // if the language param is set
    const lang = new URLSearchParams(window.location.search).get('lang');
    let preferredLang = this.localStorage.preferredLang.getValue();
    if (!!lang) {
      preferredLang = lang;
      localStorage.setPreferredLang(lang);
    }

    this.translateService.use(preferredLang);

    if (!LocalStorage.getPreferredLang()) {
      this.translateService.setDefaultLang(AppConstants.ENGLISH_CODE);
      localStorage.setPreferredLang(this.translateService.getDefaultLang());
    }

    this.translateService.use(this.localStorage.preferredLang.getValue());
    this.loadJiraFeedbackScript();
    this.loadNewrelicMonitoringScript();

    this.translateService.onLangChange.subscribe(
      () => {
        window.history.pushState('object', document.title, location.href.split(/[?#]/)[0]);
        // Reload the entire app after a language change, just in case for potential data issues
        // NOTE: Exception for the search results, we put them back to the homepage first
        //       see cps-utility-nav.ts for details
        if (location.href.indexOf('/search/') < 0) {
          location.reload();
        }
    });
  }

  ngOnInit(): void {
    this.authService.getIsUserAuthenticated().subscribe(response => {
      this.isUserAuthenticated = response;
    });

    this.initReloadTimer();
    this.initInactivityListener();

    addEventListener("DOMContentLoaded", (event) => {
      const savedDatalayer = sessionStorage.getItem('savedDatalayer');
      if (!!savedDatalayer && savedDatalayer != '') {
        const datalayer = JSON.parse(savedDatalayer);
        (<any>window).dataLayer.push(datalayer);
        // for now this var in sessionstorage is removed in document.service
      }
    });

  }

  private initReloadTimer(): void {
    const lastReload = parseInt(sessionStorage.getItem(this.LAST_RELOAD_KEY) || '0', 10);
    const now = Date.now();

    if (!lastReload || isNaN(lastReload)) {
      sessionStorage.setItem(this.LAST_RELOAD_KEY, now.toString());
    }

    const timeSinceLastReload = now - lastReload;
    const remainingTime = this.UPTIME_LIMIT - timeSinceLastReload;

    this.checkInactivityAfterUptimeTimer = setTimeout(() => {
      this.isEligibleForReload = true;
      this.startInactivityCountdown();
    }, Math.max(0, remainingTime));
  }

  private initInactivityListener(): void {
    ['mousemove', 'keydown', 'mousedown', 'touchstart'].forEach(event =>
      window.addEventListener(event, this.resetInactivityTimer)
    );

    if (this.isEligibleForReload) {
      this.startInactivityCountdown();
    }
  }

  private resetInactivityTimer = (): void => {
    clearTimeout(this.inactivityTimeout);

    if (this.isEligibleForReload) {
      this.startInactivityCountdown();
    }
  };

  private startInactivityCountdown(): void {
    clearTimeout(this.inactivityTimeout);
    this.inactivityTimeout = setTimeout(() => {
      if (this.isEligibleForReload) {
        this.reloadApp();
      }
    }, this.INACTIVITY_LIMIT);
  }

  private reloadApp(): void {
    sessionStorage.setItem('scrollpos', window.scrollY.toString());
    sessionStorage.setItem('stopAnalytics', 'true');
    sessionStorage.setItem(this.LAST_RELOAD_KEY, Date.now().toString());
    location.reload();
  }

  loadJiraFeedbackScript() {
    let node = document.createElement('script');
    node.src = this.translateService.currentLang.toLowerCase() == 'en'
    ? 'https://cpha.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/tod1zk/b/5/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-GB&collectorId=e24c1197'
    : 'https://cpha.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/tod1zk/b/6/c95134bc67d3a521bb3f4331beb9b804/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=fr-CA&collectorId=ca2e2f9e';
    node.type = 'text/javascript'; // set the script type
    node.async = true; // makes script run asynchronously
    node.charset = 'utf-8';
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  loadNewrelicMonitoringScript() {
    let node = document.createElement('script');
    node.src = environment.production
      ? '/assets/scripts/newrelic-prod.js'
      : '/assets/scripts/newrelic.js';
    node.type = 'text/javascript'; // set the script type
    node.async = true; // makes script run asynchronously
    node.charset = 'utf-8';
    // append to head of document
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  ngOnDestroy(): void {
    clearTimeout(this.inactivityTimeout);
    clearTimeout(this.checkInactivityAfterUptimeTimer);

    ['mousemove', 'keydown', 'mousedown', 'touchstart'].forEach(event =>
      window.removeEventListener(event, this.resetInactivityTimer)
    );
  }
}
